import { Globe, FileText, Eye, BarChart2, Lock } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { useSites } from '@/hooks/useSites'
import { useTotalPageCount, usePageViewCount, useSiteViewStats } from '@/hooks/useAnalytics'
import { Link } from 'react-router-dom'
import { tenantCanViewAnalytics } from '@brix/types'

function greeting() {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 18) return 'Good afternoon'
  return 'Good evening'
}

export function OverviewPage() {
  const tenant = useAuthStore((s) => s.tenant)
  const { data: sites = [] } = useSites()
  const { data: totalPages } = useTotalPageCount()
  const systemAnalytics = import.meta.env['VITE_ANALYTICS_ENABLED'] === 'true'
  const analyticsEnabled = systemAnalytics && !!tenant && tenantCanViewAnalytics(tenant)
  const { data: pageViews } = usePageViewCount(analyticsEnabled)
  const { data: siteViews = {} } = useSiteViewStats(analyticsEnabled)

  const publishedSites = sites.filter((s) => s.published).length
  const totalSites = sites.length

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          {greeting()}, {tenant?.name}
        </h1>
        <p className="mt-1 text-sm text-gray-500">Here's what's happening with your websites.</p>
      </div>

      {/* Stats */}
      <div className={`grid gap-4 mb-8 ${systemAnalytics ? 'grid-cols-4' : 'grid-cols-3'}`}>
        {[
          { label: 'Total sites', value: totalSites, icon: Globe, color: 'text-violet-600 bg-violet-50' },
          { label: 'Published', value: publishedSites, icon: Eye, color: 'text-green-600 bg-green-50' },
          { label: 'Pages', value: totalPages ?? '—', icon: FileText, color: 'text-blue-600 bg-blue-50' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-gray-500">{label}</span>
              <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${color}`}>
                <Icon className="h-4 w-4" />
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900">{value}</p>
          </div>
        ))}

        {/* Page views — only when VITE_ANALYTICS_ENABLED=true, plan-gated */}
        {systemAnalytics && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-gray-500">Page views (30d)</span>
              <div className="h-8 w-8 rounded-lg flex items-center justify-center text-orange-600 bg-orange-50">
                {analyticsEnabled ? <BarChart2 className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
              </div>
            </div>
            {analyticsEnabled ? (
              <p className="text-3xl font-bold text-gray-900">{pageViews ?? '—'}</p>
            ) : (
              <div>
                <p className="text-xl font-bold text-gray-300">—</p>
                <Link to="/billing" className="mt-1 block text-xs text-violet-600 hover:underline">
                  Upgrade to Starter to unlock
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recent sites */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="font-semibold text-gray-900">Your sites</h2>
          <Link to="/sites" className="text-sm text-violet-600 hover:underline">View all</Link>
        </div>
        {sites.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-gray-500">
            No sites yet.{' '}
            <Link to="/sites" className="text-violet-600 hover:underline">Create your first site →</Link>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {sites.slice(0, 5).map((site) => (
              <li key={site.id} className="flex items-center justify-between px-6 py-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">{site.name}</p>
                  <p className="text-xs text-gray-500">{site.subdomain}.brix.app</p>
                </div>
                <div className="flex items-center gap-3">
                  {analyticsEnabled && site.id in siteViews && (
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <BarChart2 className="h-3 w-3" />
                      {(siteViews[site.id] ?? 0).toLocaleString()} views
                    </span>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    site.published ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {site.published ? 'Published' : 'Draft'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
